/**
 * WHAT THE SERVER SENDS WHILE THE APP IS STILL ARRIVING.
 *
 * `PersistGate` renders its `loading` prop until redux-persist has rehydrated,
 * and `bootstrapped` is false on the server — so whatever is passed as
 * `loading` IS the served HTML for every route. It was `null`, which is why
 * `curl https://ordring.zenvix.cloud/` came back as
 *
 *     <div id="__next"><main class="css-i8cqgr"></main></div>
 *
 * an empty page. Nothing appeared until ~360 kB of JavaScript had downloaded,
 * parsed and rehydrated, and then the whole screen arrived at once. On a shop's
 * connection that is a long blank followed by a jolt — the "flickering" the
 * operator reported on ordring, malangjan and legendbites alike.
 *
 * This is the same markup on the server and on the client's first render, so
 * hydration matches and React never warns. The moment the store rehydrates,
 * PersistGate swaps in the real tree.
 *
 * Deliberately self-contained:
 *
 * - **No MUI, no theme.** `ThemeProvider` lives INSIDE the gate, so nothing
 *   here may read `theme.palette` — it would throw on the server.
 * - **No images and no text.** A logo would need settings this component
 *   cannot reach, and a wrong one flashing before the right one is worse than
 *   none. Text would have to be translated and would be wrong on some routes.
 * - **Fixed heights.** The blocks approximate the real shell (top bar, hero,
 *   category row, list) so the swap does not shove the page around.
 */
export default function AppSkeleton() {
  return (
    <div
      aria-hidden
      style={{
        minHeight: "100vh",
        background: BACKGROUND,
        // The shimmer keyframes have to travel with the markup: this renders
        // before any stylesheet the app itself would inject.
        padding: 0,
      }}
    >
      <style>{SHIMMER_CSS}</style>
      {/* top bar */}
      <div
        style={{
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 16px",
          background: SURFACE,
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        <Block width={92} height={22} radius={11} />
        <Block width={110} height={26} radius={13} />
        <Block width={72} height={22} radius={11} />
      </div>

      <div style={{ padding: 16, display: "grid", gap: 16 }}>
        {/* hero / banner */}
        <Block height={132} radius={12} />
        {/* category chips */}
        <div style={{ display: "flex", gap: 8, overflow: "hidden" }}>
          {[96, 112, 104, 88].map((width) => (
            <Block key={width} width={width} height={34} radius={17} />
          ))}
        </div>
        {/* section heading */}
        <Block width={150} height={20} radius={6} />
        {/* item cards */}
        <div
          style={{
            display: "grid",
            gap: 12,
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
          }}
        >
          {[0, 1, 2, 3, 4, 5].map((key) => (
            <Block key={key} height={120} radius={10} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* Neutral greys, not brand colours: the brand's palette is only known once the
   theme is up, and a guessed colour flashing to the real one is its own flicker. */
const BACKGROUND = "#FAFAFA";
const SURFACE = "#FFFFFF";
const LINE = "#EEEEEE";

const SHIMMER_CSS = `
@keyframes zenvix-skeleton {
  0%   { background-position: -180% 0; }
  100% { background-position: 180% 0; }
}
.zenvix-skeleton {
  background-image: linear-gradient(90deg, #ECECEC 25%, #F5F5F5 50%, #ECECEC 75%);
  background-size: 180% 100%;
  animation: zenvix-skeleton 1.4s ease-in-out infinite;
}
/* Respect the viewer's motion preference: a full-screen pulsing page is
   exactly the kind of thing this setting exists for. */
@media (prefers-reduced-motion: reduce) {
  .zenvix-skeleton { animation: none; background-image: none; background-color: #ECECEC; }
}`;

function Block({
  width,
  height,
  radius = 8,
}: {
  width?: number;
  height: number;
  radius?: number;
}) {
  return (
    <div
      className="zenvix-skeleton"
      style={{ width: width ?? "100%", height, borderRadius: radius }}
    />
  );
}
